import { Text } from "@chakra-ui/react";
import { Column, ColumnDef, ColumnMeta, Row } from "@tanstack/react-table";
import { format as formatDate, parse } from "date-fns";
import { ColumnDefWithTooltip } from "src/components/Table/Table";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import { DEFAULT_DATE_TYPE_FORMAT } from "src/constants/date";
import { OrganizationError } from "src/hooks/useOrganization";
import {
  formatIsoDateToOrgDate,
  formatNumberWithConstraints,
  formatUSPhoneNumber,
  maybePluralize,
} from "src/services/format";
import * as FormTemplate from "src/services/formTemplate";
import {
  getFormCompleteQuestions,
  hasCustomQuestionType,
} from "src/services/formTemplate/question";
import { isNotNull } from "src/services/predicates";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import type { RemoteData } from "src/types/remoteData";
import {
  Address,
  DisclaimerSignature,
  FormAnswer,
  FormListRow,
} from "../types";
import { getDynamicColumnId } from "./columns";

const COLUMN_MAX_WIDTH = "20em";

type QuestionColumnMeta = ColumnMeta & {
  question: AF.Question<AF.WithId>;
  nestedQuestion?: AF.Question<AF.WithId>; // For custom question nested fields
};

export function buildQuestionAnswerColumns<T extends FormListRow>({
  formTemplateGQL,
  organization,
}: {
  formTemplateGQL: GQL.FormTemplateFragment | null;
  organization: RemoteData<OrganizationError, GQL.Organization>;
}): ColumnDef<T>[] {
  if (!formTemplateGQL) return [];
  const formTemplate = FormTemplate.fromGQL(formTemplateGQL);

  const questionColumnDefs: ColumnDef<T>[] = [];

  const allQuestions = getFormCompleteQuestions(formTemplate);
  const nonArchivedQuestions = allQuestions.filter(
    (question) => !question.isArchived
  );
  nonArchivedQuestions.forEach((question) => {
    if (
      hasCustomQuestionType(question) &&
      question.nestedQuestions.length > 0
    ) {
      // For custom questions, create one column per nested question
      question.nestedQuestions.forEach((nestedQuestion) => {
        questionColumnDefs.push(
          buildQuestionAnswerColumnDef<T>(question, nestedQuestion)
        );
      });
    } else {
      // For regular questions, create one column
      questionColumnDefs.push(buildQuestionAnswerColumnDef<T>(question));
    }
  });

  const disclaimerSections = formTemplate.sections.filter(
    (section) => section.type === AF.DisclaimerSectionType
  );
  disclaimerSections.forEach((disclaimerSection) => {
    questionColumnDefs.push(
      buildDisclaimerColumnDef<T>({
        disclaimerSectionId: disclaimerSection.id,
        disclaimerTitle: disclaimerSection.title,
        organization,
      })
    );
  });

  return questionColumnDefs;
}

const questionHeader = <T extends FormListRow>(info: {
  column: Column<T, unknown>;
}) => {
  const { question, nestedQuestion } = info.column.columnDef
    .meta as QuestionColumnMeta;
  const displayText = nestedQuestion
    ? `${question.question} | ${nestedQuestion.question}`
    : question.question;
  return (
    <TextWithOverflowTooltip content={displayText} maxW={COLUMN_MAX_WIDTH}>
      <Text as="span" display="block">
        {question.question}
      </Text>
      {nestedQuestion && (
        <Text as="span" display="block">
          {nestedQuestion.question}
        </Text>
      )}
    </TextWithOverflowTooltip>
  );
};

const questionCell = <T extends FormListRow>(info: {
  column: Column<T, unknown>;
  row: Row<T>;
}) => {
  const { question, nestedQuestion } = info.column.columnDef
    .meta as QuestionColumnMeta;

  const answer = info.row.original.answers?.[question.id] ?? null;

  // Handle custom question type which has nested questions and answers
  if (
    nestedQuestion &&
    answer &&
    typeof answer === "object" &&
    "answersByQuestionId" in answer
  ) {
    const nestedAnswer = answer.answersByQuestionId[nestedQuestion.id] ?? null;
    return formatQuestionAnswer(nestedAnswer, nestedQuestion);
  }

  return formatQuestionAnswer(answer, question);
};

function buildQuestionAnswerColumnDef<T extends FormListRow>(
  question: AF.Question<AF.WithId>,
  nestedQuestion?: AF.Question<AF.WithId>
): ColumnDefWithTooltip<T> {
  const questionId = nestedQuestion
    ? `${question.id}-${nestedQuestion.id}`
    : question.id;
  return {
    id: getDynamicColumnId("question", questionId),
    meta: { question, nestedQuestion }, // Use meta to pass question to header and cell renderers, to improve performance (avoid closure function)
    header: questionHeader,
    cell: questionCell,
  };
}

function formatQuestionAnswer(
  answer: FormAnswer | null,
  question: AF.Question<AF.WithId>
): JSX.Element | string | null {
  if (!answer) return null;

  switch (question.type) {
    case AF.FreeTextType:
    case AF.EmailType:
      return (
        <TextWithOverflowTooltip
          content={answer.toString()}
          maxW={COLUMN_MAX_WIDTH}
        />
      );
    case AF.DateType:
      let dateAnswer: Date;
      if (typeof answer === "string") {
        dateAnswer = parse(answer, "yyyy-MM-dd", new Date());
      } else if (answer instanceof Date) {
        dateAnswer = answer;
      } else {
        return null;
      }
      const formattedDate = formatDate(
        dateAnswer,
        question.constraints?.format || DEFAULT_DATE_TYPE_FORMAT
      );
      return formattedDate;

    case AF.NumberType:
      let numberAnswer: number;
      if (typeof answer === "string") {
        numberAnswer = Number(answer);
      } else if (typeof answer === "number") {
        numberAnswer = answer;
      } else {
        return null;
      }
      const formattedNumber = formatNumberWithConstraints(
        numberAnswer,
        question.constraints
      );
      return formattedNumber;

    case AF.PhoneNumberType:
      return formatUSPhoneNumber(answer.toString());

    case AF.GradesType:
    case AF.SingleSelectType:
      if (typeof answer === "string") {
        const answerID = answer;
        const option = question.options.find((x) => x.id === answerID);
        if (option) {
          const isArchived = option.isArchived ?? false;
          return (
            <TextWithOverflowTooltip
              content={isArchived ? `${option.label} (archived)` : option.label}
              maxW={COLUMN_MAX_WIDTH}
              variant={isArchived ? "placeholder" : undefined}
            />
          );
        }
      }
      return null;

    case AF.MultiSelectType:
      if (Array.isArray(answer)) {
        const answerIDs = answer;
        const answerOptions = answerIDs
          .map((answerID) => {
            return question.options.find((x) => x.id === answerID);
          })
          .filter(isNotNull);
        if (answerOptions.length === 0) return null;

        const answerString = answerOptions
          .map((option) =>
            option.isArchived ? `${option.label} (archived)` : option.label
          )
          .join(", ");

        const answerElements = answerOptions.map((option) => {
          return (
            <Text
              key={option.id}
              as="span"
              variant={option.isArchived ? "placeholder" : undefined}
            >
              {option.isArchived ? `${option.label} (archived)` : option.label}
            </Text>
          );
        });

        return (
          <TextWithOverflowTooltip
            content={answerString}
            maxW={COLUMN_MAX_WIDTH}
          >
            {answerElements.map((element, index) => (
              <span key={index}>
                {element}
                {index < answerElements.length - 1 && ", "}
              </span>
            ))}
          </TextWithOverflowTooltip>
        );
      }
      return null;

    case AF.FileUploadType:
      if (Array.isArray(answer)) {
        return (
          <Text fontStyle="italic">
            {answer.length} {maybePluralize(answer.length, "file")}
          </Text>
        );
      }
      return null;

    case AF.AddressType:
      if (typeof answer === "object") {
        const addressAnswer = answer as Address;
        const addressLine1 =
          addressAnswer.street_address +
          (addressAnswer.street_address_line_2
            ? ", " + addressAnswer.street_address_line_2
            : "");
        const addressLine2 =
          addressAnswer.city +
          ", " +
          addressAnswer.state +
          " " +
          addressAnswer.zip_code;
        return (
          <>
            <Text>{addressLine1}</Text>
            <Text>{addressLine2}</Text>
          </>
        );
      }
      return null;

    default:
      return null;
  }
}

function buildDisclaimerColumnDef<T extends FormListRow>({
  disclaimerSectionId,
  disclaimerTitle,
  organization,
}: {
  disclaimerSectionId: string;
  disclaimerTitle: string;
  organization: RemoteData<OrganizationError, GQL.Organization>;
}): ColumnDefWithTooltip<T> {
  return {
    id: getDynamicColumnId("disclaimer", disclaimerSectionId),
    header: () => (
      <TextWithOverflowTooltip
        content={disclaimerTitle}
        maxW={COLUMN_MAX_WIDTH}
      />
    ),
    cell: ({ row }) => {
      const answer = row.original.answers?.[disclaimerSectionId];
      if (typeof answer === "object") {
        const disclaimer = answer as DisclaimerSignature;
        return (
          <>
            <Text>{disclaimer.signature}</Text>
            <Text fontStyle="italic" fontSize="xs">
              Signed at:{" "}
              {formatIsoDateToOrgDate(disclaimer.signedAt, organization)}
            </Text>
          </>
        );
      }
      return null;
    },
  };
}
